import BaseService from '@/services/BaseService';
import { ResponseError } from '@/lib/exceptions';
import type { ChartDataPoint } from './ChartQueryService';

export type PrivateCreditFinanceDashboardFilterOptions = {
  dealNames: string[];
};

export type PrivateCreditFinanceTapeDashboardKpiLatest = {
  asOfDateIso: string;
  totalCommitment: number;
  totalFunded: number;
  utilizationPct: number | null;
  waAdvanceRatePct: number | null;
  waClassAAdvanceRatePct: number | null;
  waSpreadPct: number | null;
  loanCount: number;
  obligorCount: number;
  facilityCount: number;
  industryCount: number;
  firstLienCommitmentPct: number | null;
  waLeverageTotal: number | null;
  waLeverageSenior: number | null;
  waCashInterestCoverage: number | null;
  loansWithMaturity: number;
  waRemainingTermYears: number | null;
};

export type PrivateCreditFinanceFacilityBalanceRow = {
  dealName: string;
  commitment: number;
  funded: number;
  unfunded: number;
};

export type PrivateCreditFinanceDealMetricByMonthRow = {
  asOfMonth: string;
  dealName: string;
  amount: number;
  /** Optional weight used for the metric (e.g. funded balance for WA spread). */
  weightAmount?: number | null;
};

export type PrivateCreditFinanceCategoryMetricByMonthRow = {
  asOfMonth: string;
  category: string;
  funded: number;
  commitment: number;
  /** All-industry funded total for the month (industries-over-time only). */
  portfolioFundedTotal?: number;
};

export type PrivateCreditFinanceCategorySlice = {
  category: string;
  value: number;
};

export type PrivateCreditFinanceBucketRow = {
  bucketLabel: string;
  funded: number;
  commitment: number;
};

export type PrivateCreditFinancePortfolioTrendRow = {
  asOfMonth: string;
  /** Facility / deal name (<c>DEAL_NAME</c>). */
  dealName: string;
  waInitialEbitda: number | null;
  waCurrentEbitda: number | null;
  waInitialTotalDebtEbitda: number | null;
  waCurrentTotalDebtEbitda: number | null;
  waInitialSeniorDebtEbitda: number | null;
  waCurrentSeniorDebtEbitda: number | null;
  waInitialCashInterestCoverage: number | null;
  waCurrentCashInterestCoverage: number | null;
};

export type PrivateCreditFinanceWatchlistSummary = {
  watchlistObligorCount: number;
  watchlistLoanCount: number;
  watchlistCommitment: number;
  portfolioCommitment: number;
  watchlistCommitmentPct: number | null;
  defaultedLoanCount: number;
  haircutLoanCount: number;
};

export type PrivateCreditFinanceWatchlistObligorRow = {
  obligorName: string;
  dealNames: string;
  industry: string;
  commitment: number;
  fundedBalance: number;
  totalDebtEbitda: number | null;
  cashInterestCoverage: number | null;
  ttmEbitda: number | null;
  flag: string;
};

export type PrivateCreditFinanceWatchlistCreditSurveillance = {
  summary: PrivateCreditFinanceWatchlistSummary;
  obligors: PrivateCreditFinanceWatchlistObligorRow[];
};

export type PrivateCreditFinanceTopObligorRow = {
  obligorName: string;
  industry: string;
  commitment: number;
  fundedBalance: number;
  portfolioCommitmentPct: number | null;
  currentTotalDebtEbitda: number | null;
  cashInterestCoverage: number | null;
  ttmEbitda: number | null;
};

export type PrivateCreditFinanceSpIndustryExposureRow = {
  rank: number;
  spIndustry: string;
  totalExposurePctOfOpb: number;
  waSeniorLeverage: number | null;
  waTotalLeverage: number | null;
  waEbitda: number | null;
};

export type PrivateCreditFinanceCompositionDimension = 'loanType' | 'lienType' | 'obligorTier';

const FILTER_OPTIONS_PATH = 'private-credit-finance-dashboard/filter-options';

function stringArrayFromBody(body: Record<string, unknown>, camelKey: string, pascalKey: string): string[] {
  const raw = body[camelKey] ?? body[pascalKey];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((x): x is string => typeof x === 'string');
}

function parseNum(v: unknown): number | null {
  if (v == null) {
    return null;
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function parseInt64(v: unknown): number | null {
  const n = parseNum(v);
  return n == null ? null : Math.trunc(n);
}

function appendDealNames(qs: URLSearchParams, dealNames: readonly string[]) {
  for (const d of dealNames) {
    const t = d.trim();
    if (t) {
      qs.append('dealName', t);
    }
  }
}

function appendAsOfDate(qs: URLSearchParams, asOfDate: string) {
  const t = asOfDate.trim();
  if (t) {
    qs.set('asOfDate', t);
  }
}

export type PrivateCreditFinanceDashboardFilters = {
  dealName: readonly string[];
  asOfDate: string;
};

function parseKpiRow(raw: unknown): PrivateCreditFinanceTapeDashboardKpiLatest | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const asOf = o.asOfDateIso ?? o.AsOfDateIso;
  if (typeof asOf !== 'string' || asOf.length === 0) {
    return null;
  }
  const totalCommitment = parseNum(o.totalCommitment ?? o.TotalCommitment);
  const totalFunded = parseNum(o.totalFunded ?? o.TotalFunded);
  const loanCount = parseInt64(o.loanCount ?? o.LoanCount);
  if (totalCommitment == null || totalFunded == null || loanCount == null) {
    return null;
  }

  return {
    asOfDateIso: asOf,
    totalCommitment,
    totalFunded,
    utilizationPct: parseNum(o.utilizationPct ?? o.UtilizationPct),
    waAdvanceRatePct: parseNum(o.waAdvanceRatePct ?? o.WaAdvanceRatePct),
    waClassAAdvanceRatePct: parseNum(o.waClassAAdvanceRatePct ?? o.WaClassAAdvanceRatePct),
    waSpreadPct: parseNum(o.waSpreadPct ?? o.WaSpreadPct),
    loanCount,
    obligorCount: parseInt64(o.obligorCount ?? o.ObligorCount) ?? 0,
    facilityCount: parseInt64(o.facilityCount ?? o.FacilityCount) ?? 0,
    industryCount: parseInt64(o.industryCount ?? o.IndustryCount) ?? 0,
    firstLienCommitmentPct: parseNum(o.firstLienCommitmentPct ?? o.FirstLienCommitmentPct),
    waLeverageTotal: parseNum(o.waLeverageTotal ?? o.WaLeverageTotal),
    waLeverageSenior: parseNum(o.waLeverageSenior ?? o.WaLeverageSenior),
    waCashInterestCoverage: parseNum(o.waCashInterestCoverage ?? o.WaCashInterestCoverage),
    loansWithMaturity: parseInt64(o.loansWithMaturity ?? o.LoansWithMaturity) ?? 0,
    waRemainingTermYears: parseNum(o.waRemainingTermYears ?? o.WaRemainingTermYears),
  };
}

function parseFacilityBalanceRows(raw: unknown): PrivateCreditFinanceFacilityBalanceRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: PrivateCreditFinanceFacilityBalanceRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const dealName = r.dealName ?? r.DealName;
    if (typeof dealName !== 'string' || dealName.length === 0) {
      continue;
    }
    const commitment = parseNum(r.commitment ?? r.Commitment);
    const funded = parseNum(r.funded ?? r.Funded);
    const unfunded = parseNum(r.unfunded ?? r.Unfunded);
    if (commitment == null || funded == null || unfunded == null) {
      continue;
    }
    out.push({ dealName, commitment, funded, unfunded });
  }
  return out;
}

function parseCategorySlices(raw: unknown): PrivateCreditFinanceCategorySlice[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.slices ?? o.Slices;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: PrivateCreditFinanceCategorySlice[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const category = r.category ?? r.Category;
    const value = parseNum(r.value ?? r.Value);
    if (typeof category !== 'string' || category.length === 0 || value == null) {
      continue;
    }
    out.push({ category, value });
  }
  return out;
}

function parseBucketRows(raw: unknown): PrivateCreditFinanceBucketRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: PrivateCreditFinanceBucketRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const bucketLabel = r.bucketLabel ?? r.BucketLabel;
    const funded = parseNum(r.funded ?? r.Funded) ?? 0;
    const commitment = parseNum(r.commitment ?? r.Commitment ?? r.amount ?? r.Amount);
    if (typeof bucketLabel !== 'string' || bucketLabel.length === 0 || commitment == null) {
      continue;
    }
    out.push({ bucketLabel, funded, commitment });
  }
  return out;
}

function parseDealMetricByMonthRows(raw: unknown): PrivateCreditFinanceDealMetricByMonthRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: PrivateCreditFinanceDealMetricByMonthRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const asOfMonth = r.asOfMonth ?? r.AsOfMonth;
    const dealName = r.dealName ?? r.DealName;
    const amount = parseNum(r.amount ?? r.Amount);
    if (typeof asOfMonth !== 'string' || asOfMonth.length === 0) {
      continue;
    }
    if (typeof dealName !== 'string' || dealName.length === 0) {
      continue;
    }
    if (amount == null) {
      continue;
    }
    out.push({
      asOfMonth,
      dealName,
      amount,
      weightAmount: parseNum(r.weightAmount ?? r.WeightAmount),
    });
  }
  return out;
}

/** Pivot API rows into AutoChart stacked-bar points (month × facility). */
export function privateCreditFinanceDealMetricRowsToChartDataPoints(
  rows: readonly PrivateCreditFinanceDealMetricByMonthRow[],
): ChartDataPoint[] {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const key = `${row.asOfMonth}\u0001${row.dealName}`;
    totals.set(key, (totals.get(key) ?? 0) + row.amount);
  }

  const points: ChartDataPoint[] = [];
  for (const [key, value] of totals) {
    const sep = key.indexOf('\u0001');
    points.push({
      xAxis: key.slice(0, sep),
      series: key.slice(sep + 1),
      value,
    });
  }
  return points;
}

function parsePortfolioTrendRows(raw: unknown): PrivateCreditFinancePortfolioTrendRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: PrivateCreditFinancePortfolioTrendRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const asOfMonth = r.asOfMonth ?? r.AsOfMonth;
    const dealNameRaw = r.dealName ?? r.DealName;
    if (typeof asOfMonth !== 'string' || asOfMonth.length === 0) {
      continue;
    }
    if (typeof dealNameRaw !== 'string' || dealNameRaw.length === 0) {
      continue;
    }
    out.push({
      asOfMonth,
      dealName: dealNameRaw,
      waInitialEbitda: parseNum(r.waInitialEbitda ?? r.WaInitialEbitda),
      waCurrentEbitda: parseNum(r.waCurrentEbitda ?? r.WaCurrentEbitda),
      waInitialTotalDebtEbitda: parseNum(r.waInitialTotalDebtEbitda ?? r.WaInitialTotalDebtEbitda),
      waCurrentTotalDebtEbitda: parseNum(r.waCurrentTotalDebtEbitda ?? r.WaCurrentTotalDebtEbitda),
      waInitialSeniorDebtEbitda: parseNum(r.waInitialSeniorDebtEbitda ?? r.WaInitialSeniorDebtEbitda),
      waCurrentSeniorDebtEbitda: parseNum(r.waCurrentSeniorDebtEbitda ?? r.WaCurrentSeniorDebtEbitda),
      waInitialCashInterestCoverage: parseNum(r.waInitialCashInterestCoverage ?? r.WaInitialCashInterestCoverage),
      waCurrentCashInterestCoverage: parseNum(r.waCurrentCashInterestCoverage ?? r.WaCurrentCashInterestCoverage),
    });
  }
  return out;
}

function parseWatchlistSummary(raw: unknown): PrivateCreditFinanceWatchlistSummary {
  const r = raw != null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    watchlistObligorCount: parseInt64(r.watchlistObligorCount ?? r.WatchlistObligorCount) ?? 0,
    watchlistLoanCount: parseInt64(r.watchlistLoanCount ?? r.WatchlistLoanCount) ?? 0,
    watchlistCommitment: parseNum(r.watchlistCommitment ?? r.WatchlistCommitment) ?? 0,
    portfolioCommitment: parseNum(r.portfolioCommitment ?? r.PortfolioCommitment) ?? 0,
    watchlistCommitmentPct: parseNum(r.watchlistCommitmentPct ?? r.WatchlistCommitmentPct),
    defaultedLoanCount: parseInt64(r.defaultedLoanCount ?? r.DefaultedLoanCount) ?? 0,
    haircutLoanCount: parseInt64(r.haircutLoanCount ?? r.HaircutLoanCount) ?? 0,
  };
}

function parseWatchlistObligorRows(raw: unknown): PrivateCreditFinanceWatchlistObligorRow[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: PrivateCreditFinanceWatchlistObligorRow[] = [];
  for (const item of raw) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const obligorName = r.obligorName ?? r.ObligorName;
    if (typeof obligorName !== 'string' || obligorName.length === 0) {
      continue;
    }
    const flagRaw = r.flag ?? r.Flag;
    out.push({
      obligorName,
      dealNames: String(r.dealNames ?? r.DealNames ?? ''),
      industry: String(r.industry ?? r.Industry ?? 'Other'),
      commitment: parseNum(r.commitment ?? r.Commitment) ?? 0,
      fundedBalance: parseNum(r.fundedBalance ?? r.FundedBalance) ?? 0,
      totalDebtEbitda: parseNum(r.totalDebtEbitda ?? r.TotalDebtEbitda),
      cashInterestCoverage: parseNum(r.cashInterestCoverage ?? r.CashInterestCoverage),
      ttmEbitda: parseNum(r.ttmEbitda ?? r.TtmEbitda),
      flag: typeof flagRaw === 'string' && flagRaw.length > 0 ? flagRaw : 'REVIEW',
    });
  }
  return out;
}

function parseWatchlistCreditSurveillance(raw: unknown): PrivateCreditFinanceWatchlistCreditSurveillance {
  if (raw == null || typeof raw !== 'object') {
    return {
      summary: parseWatchlistSummary(null),
      obligors: [],
    };
  }
  const o = raw as Record<string, unknown>;
  const data = o.data ?? o.Data ?? o;
  const payload = data != null && typeof data === 'object' ? (data as Record<string, unknown>) : o;
  const summary = payload.summary ?? payload.Summary;
  const obligors = payload.obligors ?? payload.Obligors;
  return {
    summary: parseWatchlistSummary(summary),
    obligors: parseWatchlistObligorRows(obligors),
  };
}

function parseTopObligorRows(raw: unknown): PrivateCreditFinanceTopObligorRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: PrivateCreditFinanceTopObligorRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const obligorName = r.obligorName ?? r.ObligorName;
    if (typeof obligorName !== 'string' || obligorName.length === 0) {
      continue;
    }
    out.push({
      obligorName,
      industry: String(r.industry ?? r.Industry ?? 'Other'),
      commitment: parseNum(r.commitment ?? r.Commitment) ?? 0,
      fundedBalance: parseNum(r.fundedBalance ?? r.FundedBalance) ?? 0,
      portfolioCommitmentPct: parseNum(r.portfolioCommitmentPct ?? r.PortfolioCommitmentPct),
      currentTotalDebtEbitda: parseNum(r.currentTotalDebtEbitda ?? r.CurrentTotalDebtEbitda),
      cashInterestCoverage: parseNum(r.cashInterestCoverage ?? r.CashInterestCoverage),
      ttmEbitda: parseNum(r.ttmEbitda ?? r.TtmEbitda),
    });
  }
  return out;
}

function parseSpIndustryExposureRows(raw: unknown): PrivateCreditFinanceSpIndustryExposureRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: PrivateCreditFinanceSpIndustryExposureRow[] = [];
  for (const [index, item] of list.entries()) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const spIndustry = r.spIndustry ?? r.SpIndustry;
    if (typeof spIndustry !== 'string' || spIndustry.length === 0) {
      continue;
    }
    out.push({
      rank: index + 1,
      spIndustry,
      totalExposurePctOfOpb: parseNum(r.totalExposurePctOfOpb ?? r.TotalExposurePctOfOpb) ?? 0,
      waSeniorLeverage: parseNum(r.waSeniorLeverage ?? r.WaSeniorLeverage),
      waTotalLeverage: parseNum(r.waTotalLeverage ?? r.WaTotalLeverage),
      waEbitda: parseNum(r.waEbitda ?? r.WaEbitda),
    });
  }
  return out;
}

function parseCategoryMetricByMonthRows(raw: unknown): PrivateCreditFinanceCategoryMetricByMonthRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: PrivateCreditFinanceCategoryMetricByMonthRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const asOfMonth = r.asOfMonth ?? r.AsOfMonth;
    const category = r.category ?? r.Category;
    const funded = parseNum(r.funded ?? r.Funded ?? r.amount ?? r.Amount);
    const commitment = parseNum(r.commitment ?? r.Commitment);
    const portfolioFundedTotal = parseNum(r.portfolioFundedTotal ?? r.PortfolioFundedTotal);
    if (typeof asOfMonth !== 'string' || asOfMonth.length === 0) {
      continue;
    }
    if (typeof category !== 'string' || category.length === 0) {
      continue;
    }
    if (funded == null) {
      continue;
    }
    out.push({
      asOfMonth,
      category,
      funded,
      commitment: commitment ?? 0,
      ...(portfolioFundedTotal != null ? { portfolioFundedTotal } : {}),
    });
  }
  return out;
}

/** Pivot API rows into AutoChart stacked-bar points (month × category; bar = funded, companion = commitment). */
export function privateCreditFinanceCategoryMetricRowsToChartDataPoints(
  rows: readonly PrivateCreditFinanceCategoryMetricByMonthRow[],
): ChartDataPoint[] {
  const fundedByKey = new Map<string, number>();
  const commitmentByKey = new Map<string, number>();
  const portfolioFundedByMonth = new Map<string, number>();
  for (const row of rows) {
    const key = `${row.asOfMonth}\u0001${row.category}`;
    fundedByKey.set(key, (fundedByKey.get(key) ?? 0) + row.funded);
    commitmentByKey.set(key, (commitmentByKey.get(key) ?? 0) + row.commitment);
    if (row.portfolioFundedTotal != null && Number.isFinite(row.portfolioFundedTotal)) {
      portfolioFundedByMonth.set(row.asOfMonth, row.portfolioFundedTotal);
    }
  }

  const points: ChartDataPoint[] = [];
  for (const [key, funded] of fundedByKey) {
    const sep = key.indexOf('\u0001');
    const xAxis = key.slice(0, sep);
    const portfolioTotal = portfolioFundedByMonth.get(xAxis);
    points.push({
      xAxis,
      series: key.slice(sep + 1),
      value: funded,
      companionValue: commitmentByKey.get(key) ?? 0,
      ...(portfolioTotal != null ? { portfolioShareDenominator: portfolioTotal } : {}),
    });
  }
  return points;
}

/** Funded portfolio share (0–1) per month for percent-line charts; retains funded + commitment for tooltips. */
export function privateCreditFinanceCategoryMetricRowsToFundedPercentLineChartDataPoints(
  rows: readonly PrivateCreditFinanceCategoryMetricByMonthRow[],
): ChartDataPoint[] {
  const absolute = privateCreditFinanceCategoryMetricRowsToChartDataPoints(rows);
  const totalFundedByMonth = new Map<string, number>();
  for (const point of absolute) {
    totalFundedByMonth.set(point.xAxis, (totalFundedByMonth.get(point.xAxis) ?? 0) + point.value);
  }

  return absolute.map(point => {
    const totalFunded = totalFundedByMonth.get(point.xAxis) ?? 0;
    return {
      xAxis: point.xAxis,
      series: point.series,
      value: totalFunded > 0 ? point.value / totalFunded : 0,
      portfolioShareNumerator: point.value,
      portfolioShareDenominator: totalFunded,
      companionValue: point.companionValue,
    };
  });
}

export class PrivateCreditFinanceDashboardService extends BaseService {
  constructor() {
    super('/api/digitalconnect-service');
  }

  getJsonBody = async (path: string) => {
    const raw = await this.get({ path: path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      return (await raw.json()) as Record<string, unknown>;
    }
    if (raw && typeof raw === 'object') {
      return raw as Record<string, unknown>;
    }
    return {};
  };

  fetchWithDealFilter = async (
    pathBase: string,
    filters: PrivateCreditFinanceDashboardFilters,
  ): Promise<Record<string, unknown>> => {
    const qs = new URLSearchParams();
    appendAsOfDate(qs, filters.asOfDate);
    appendDealNames(qs, filters.dealName);
    const q = qs.toString();
    const path = q.length > 0 ? `${pathBase}?${q}` : pathBase;
    return this.getJsonBody(path);
  };

  fetchPrivateCreditFinanceDashboardFilterOptions = async () => {
    const body = await this.getJsonBody(FILTER_OPTIONS_PATH);
    return {
      dealNames: stringArrayFromBody(body, 'dealNames', 'DealNames'),
    };
  };

  fetchPrivateCreditFinanceDashboardAsOfDates = async () => {
    const body = await this.getJsonBody('private-credit-finance-dashboard/as-of-dates');
    return stringArrayFromBody(body, 'asOfDates', 'AsOfDates');
  };

  fetchPrivateCreditFinanceTapeDashboardKpi = async (filters: PrivateCreditFinanceDashboardFilters) => {
    const qs = new URLSearchParams();
    appendAsOfDate(qs, filters.asOfDate);
    appendDealNames(qs, filters.dealName);
    const q = qs.toString();
    const path =
      q.length > 0 ? `private-credit-finance-dashboard/tape-kpi?${q}` : 'private-credit-finance-dashboard/tape-kpi';

    const raw = await this.get({ path: path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return null;
      }
      return parseKpiRow(JSON.parse(text) as unknown);
    }
    return parseKpiRow(raw);
  };

  fetchPrivateCreditFinanceFacilityBalances = async (filters: PrivateCreditFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter('private-credit-finance-dashboard/facility-balances', filters);
    return parseFacilityBalanceRows(body);
  };

  fetchPrivateCreditFinanceCompositionSlices = async (
    dimension: PrivateCreditFinanceCompositionDimension,
    filters: PrivateCreditFinanceDashboardFilters,
  ) => {
    const qs = new URLSearchParams();
    qs.set('dimension', dimension);
    appendAsOfDate(qs, filters.asOfDate);
    appendDealNames(qs, filters.dealName);
    const body = await this.getJsonBody(`private-credit-finance-dashboard/composition-slices?${qs.toString()}`);
    return parseCategorySlices(body);
  };

  fetchPrivateCreditFinanceTopIndustries = async (filters: PrivateCreditFinanceDashboardFilters, limit = 10) => {
    const qs = new URLSearchParams();
    qs.set('limit', String(limit));
    appendAsOfDate(qs, filters.asOfDate);
    appendDealNames(qs, filters.dealName);
    const body = await this.getJsonBody(`private-credit-finance-dashboard/top-industries?${qs.toString()}`);
    return parseCategorySlices(body);
  };

  fetchPrivateCreditFinanceLeverageDistribution = async (filters: PrivateCreditFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter('private-credit-finance-dashboard/leverage-distribution', filters);
    return parseBucketRows(body);
  };

  fetchPrivateCreditFinanceInterestCoverageDistribution = async (filters: PrivateCreditFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter(
      'private-credit-finance-dashboard/interest-coverage-distribution',
      filters,
    );
    return parseBucketRows(body);
  };

  fetchPrivateCreditFinanceAdvanceRateDistribution = async (filters: PrivateCreditFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter('private-credit-finance-dashboard/advance-rate-distribution', filters);
    return parseBucketRows(body);
  };

  fetchPrivateCreditFinanceMaturityByYear = async (filters: PrivateCreditFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter('private-credit-finance-dashboard/maturity-by-year', filters);
    return parseBucketRows(body);
  };

  fetchPrivateCreditFinanceCommitmentByDealOverTime = async (filters: PrivateCreditFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter(
      'private-credit-finance-dashboard/commitment-by-deal-over-time',
      filters,
    );
    return parseDealMetricByMonthRows(body);
  };

  fetchPrivateCreditFinanceFundedByDealOverTime = async (filters: PrivateCreditFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter('private-credit-finance-dashboard/funded-by-deal-over-time', filters);
    return parseDealMetricByMonthRows(body);
  };

  fetchPrivateCreditFinanceSpreadByDealOverTime = async (filters: PrivateCreditFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter('private-credit-finance-dashboard/spread-by-deal-over-time', filters);
    return parseDealMetricByMonthRows(body);
  };

  fetchPrivateCreditFinanceLoanCountByDealOverTime = async (filters: PrivateCreditFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter(
      'private-credit-finance-dashboard/loan-count-by-deal-over-time',
      filters,
    );
    return parseDealMetricByMonthRows(body);
  };

  fetchPrivateCreditFinancePortfolioTrendsOverTime = async (filters: PrivateCreditFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter('private-credit-finance-dashboard/portfolio-trends-over-time', filters);
    return parsePortfolioTrendRows(body);
  };

  fetchPrivateCreditFinanceWatchlistCreditSurveillance = async (
    filters: PrivateCreditFinanceDashboardFilters,
    limit = 15,
  ) => {
    const qs = new URLSearchParams();
    qs.set('limit', String(limit));
    appendAsOfDate(qs, filters.asOfDate);
    appendDealNames(qs, filters.dealName);
    const body = await this.getJsonBody(
      `private-credit-finance-dashboard/watchlist-credit-surveillance?${qs.toString()}`,
    );
    return parseWatchlistCreditSurveillance(body);
  };

  fetchPrivateCreditFinanceTopObligors = async (filters: PrivateCreditFinanceDashboardFilters, limit = 10) => {
    const qs = new URLSearchParams();
    qs.set('limit', String(limit));
    appendAsOfDate(qs, filters.asOfDate);
    appendDealNames(qs, filters.dealName);
    const body = await this.getJsonBody(`private-credit-finance-dashboard/top-obligors?${qs.toString()}`);
    return parseTopObligorRows(body);
  };

  fetchPrivateCreditFinanceTopSpIndustryExposure = async (
    filters: PrivateCreditFinanceDashboardFilters,
    limit = 10,
  ) => {
    const qs = new URLSearchParams();
    qs.set('limit', String(limit));
    appendAsOfDate(qs, filters.asOfDate);
    appendDealNames(qs, filters.dealName);
    const body = await this.getJsonBody(`private-credit-finance-dashboard/top-sp-industry-exposure?${qs.toString()}`);
    return parseSpIndustryExposureRows(body);
  };

  fetchPrivateCreditFinanceCompositionOverTime = async (
    dimension: PrivateCreditFinanceCompositionDimension,
    filters: PrivateCreditFinanceDashboardFilters,
  ) => {
    const qs = new URLSearchParams();
    qs.set('dimension', dimension);
    appendAsOfDate(qs, filters.asOfDate);
    appendDealNames(qs, filters.dealName);
    const body = await this.getJsonBody(`private-credit-finance-dashboard/composition-over-time?${qs.toString()}`);
    return parseCategoryMetricByMonthRows(body);
  };

  fetchPrivateCreditFinanceIndustriesOverTime = async (filters: PrivateCreditFinanceDashboardFilters, limit = 10) => {
    const qs = new URLSearchParams();
    qs.set('limit', String(limit));
    appendAsOfDate(qs, filters.asOfDate);
    appendDealNames(qs, filters.dealName);
    const body = await this.getJsonBody(`private-credit-finance-dashboard/industries-over-time?${qs.toString()}`);
    return parseCategoryMetricByMonthRows(body);
  };
}
